import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { getFixOrderEntries, removeDuplicateOrderEntries } from "../../services/orderService";
import { formatAmountLegacy } from "../../utils/formatters";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";

const ISSUE_OPTIONS = [
  { value: "duplicates", label: "Duplicates (same refNo + orderItemId)" },
  { value: "missing_order_item_id", label: "Missing orderItemId" },
  { value: "missing_product_sku", label: "Missing productId / SKU" },
];

const limit = 15;

const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const FixOrdersEntries = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [issueType, setIssueType] = useState("duplicates");
  const [isRemoving, setIsRemoving] = useState(false);

  const { data, isLoading } = useQuery(
    ["fix-order-entries", page, issueType],
    () => getFixOrderEntries({ page, limit, issueType, sortField: "id", sortValue: "ASC" }),
  );

  const orders = data?.data ?? [];
  const count = data?.count ?? 0;
  const hasNext = data?.hasNext ?? false;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/orders")}
            className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← Back to Orders
          </button>
          <h1 className="text-2xl font-bold">Fix Order Entries</h1>
        </div>
        <div className="text-sm text-gray-500">
          Total entries: {count}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Issue type</label>
        <div className="flex flex-wrap justify-between items-center gap-3">
          <select
            className="select select-bordered w-full max-w-md"
            value={issueType}
            onChange={(e) => {
              setIssueType(e.target.value);
              setPage(1);
            }}
          >
            {ISSUE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {issueType === "duplicates" && (
            <button
              type="button"
              onClick={async () => {
                setIsRemoving(true);
                try {
                  const res = await removeDuplicateOrderEntries();
                  const deleted = res?.data?.deletedCount ?? 0;
                  toast.success(res?.message || `Removed ${deleted} duplicate(s).`);
                  await queryClient.invalidateQueries(["fix-order-entries"]);
                  if (deleted > 0) setPage(1);
                } catch (err) {
                  toast.error(err?.message || "Failed to remove duplicates");
                } finally {
                  setIsRemoving(false);
                }
              }}
              disabled={isRemoving || count === 0}
              className="btn btn-error btn-sm text-white"
            >
              {isRemoving ? "Removing…" : "Remove all duplicates"}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No entries found for this filter.
            </div>
          ) : (
            <table className="table table-zebra w-full text-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Ref No</th>
                  <th>Order Item ID</th>
                  <th>Product ID</th>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Ref Date</th>
                  <th>Platform</th>
                  <th>Qty</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="font-mono">{order.id}</td>
                    <td className="whitespace-nowrap">
                      <span
                        className="cursor-pointer text-blue-500 hover:text-blue-600"
                        onClick={() => navigate(`/orders/detail/${order.refNo}`)}
                      >
                        {order.refNo || "—"}
                      </span>
                    </td>
                    <td className="font-mono text-xs">{order.orderItemId ?? "—"}</td>
                    <td>{order.productId ?? "—"}</td>
                    <td className="font-mono text-xs">{order.sku ?? "—"}</td>
                    <td className="max-w-[200px] truncate" title={order.productName}>
                      {order.productName || "—"}
                    </td>
                    <td className="whitespace-nowrap">{formatDate(order.refDate)}</td>
                    <td className="capitalize">{order.postingAccount || "—"}</td>
                    <td>{order.qty ?? "—"}</td>
                    <td>{order.netAmount != null ? formatAmountLegacy(order.netAmount) : "—"}</td>
                    <td>
                      <span className="badge badge-outline capitalize text-xs">
                        {order.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!isLoading && count > 0 && (
          <Pagination
            page={page}
            onPageChange={setPage}
            limit={limit}
            totalCount={count}
            hasNext={hasNext}
          />
        )}
      </div>
    </div>
  );
};

export default FixOrdersEntries;
